import { HttpInterceptorFn } from '@angular/common/http';
import { environment } from '../../../environments/environment';

/**
 * Server-only rewrite of relative `/api/v1/*` calls onto the api-gateway.
 *
 * The gateway routes by service prefix (`/<service>/**`, StripPrefix=1) but the
 * app issues prefix-less relative calls (`/api/v1/tickets`, …) that in local dev
 * are routed per-path by src/proxy.conf.json. This interceptor reproduces that
 * path→service map so the SAME relative calls work against the gateway:
 *
 *   /api/v1/tickets/42  →  {apiBaseUrl}/ticket-service/api/v1/tickets/42
 *
 * No-op unless environment.apiBaseUrl is set (i.e. the production build). In dev
 * apiBaseUrl is '' and proxy.conf.json does the routing, so this passes through.
 *
 * KEEP IN SYNC with src/proxy.conf.json. Longest-prefix wins; anything under
 * /api not matched falls through to booking-service (the proxy.conf.json `/api`
 * catch-all → :8110).
 */
const SERVICE_ROUTES: ReadonlyArray<readonly [string, string]> = [
  ['/api/v1/notification-rules', 'notification-service'],
  ['/api/v1/tickets', 'ticket-service'],
  ['/api/v1/chat', 'chat-service'],
  ['/api/v1/cip', 'cip-service'],
  ['/api/v1/issues', 'issue-service'],
  // identity-service
  ['/api/v1/me', 'identity-service'],
  ['/api/v1/auth', 'identity-service'],
  ['/api/v1/users', 'identity-service'],
  ['/api/v1/staff', 'identity-service'],
  ['/api/v1/permissions', 'identity-service'],
  ['/api/v1/agency-roles', 'identity-service'],
  ['/api/v1/platform-users', 'identity-service'],
  // agency-service
  ['/api/v1/agencies', 'agency-service'],
  ['/api/v1/agency-groups', 'agency-service'],
  ['/api/v1/signs', 'agency-service'],
  ['/api/v1/sign-access', 'agency-service'],
  ['/api/v1/sale-channels', 'agency-service'],
  ['/api/v1/channel-access', 'agency-service'],
  ['/api/v1/companies', 'agency-service'],
  ['/api/v1/contract-templates', 'agency-service'],
  ['/api/v1/partnerships', 'agency-service'],
  ['/api/v1/branches', 'agency-service'],
  ['/api/v1/counters', 'agency-service'],
  ['/api/v1/system', 'agency-service'],
  ['/api/v1/pricing', 'agency-service'],
  // other domains
  ['/api/v1/content', 'content-service'],
  ['/api/v1/suppliers', 'supplier-service'],
  ['/api/v1/customers', 'customer-service'],
  ['/api/v1/customer-groups', 'customer-service'],
  ['/api/v1/loyalty', 'customer-service'],
  ['/api/v1/visas', 'visa-service'],
];
const DEFAULT_SERVICE = 'booking-service';

function resolveService(path: string): string {
  for (const [prefix, service] of SERVICE_ROUTES) {
    if (path === prefix || path.startsWith(prefix + '/')) return service;
  }
  return DEFAULT_SERVICE;
}

export const gatewayUrlInterceptor: HttpInterceptorFn = (req, next) => {
  const base = environment.apiBaseUrl;
  // Dev (base empty) or non-/api calls (assets, absolute BaseUrls) → passthrough.
  if (!base || !req.url.startsWith('/api/')) return next(req);

  const path = req.url.split('?')[0];
  const service = resolveService(path);
  return next(req.clone({ url: `${base}/${service}${req.url}` }));
};
